import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { trackPaymentMethodAdded, trackEvent } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const { data } = await axios.get(`${API}/payments/checkout/status/${sid}`);
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        trackEvent('payment_completed', {
          event_category: 'conversion',
          value: (data.amount_total || 0) / 100,
          currency: 'USD',
          campaign_id: data.metadata?.campaign_id,
        });
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'checking' || status === 'processing' ? (
          <div className="glass p-12">
            <Loader2 className="w-12 h-12 text-[#FF4343] mx-auto mb-4 animate-spin" />
            <h2 className="font-display text-2xl font-bold mb-2">Processing Payment</h2>
            <p className="text-[#9CA3AF] text-sm">Verifying your payment status...</p>
          </div>
        ) : status === 'success' ? (
          <div className="glass p-12" data-testid="payment-success-card">
            <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-6" />
            <h2 className="font-display text-3xl font-bold mb-3">Payment <span className="text-[#10B981]">Successful</span></h2>
            <p className="text-[#9CA3AF] text-sm mb-2">Your campaign has been funded successfully.</p>
            {paymentData && (
              <div className="bg-[#090E33] border border-white/10 p-4 mt-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#6B7280]">Amount</span>
                  <span className="font-mono text-[#FF4343]">
                    ${((paymentData.amount_total || 0) / 100).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#6B7280]">Campaign</span>
                  <span className="text-white text-xs">{paymentData.metadata?.campaign_title || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B7280]">Status</span>
                  <span className="font-mono text-xs text-[#10B981] uppercase">{paymentData.payment_status}</span>
                </div>
              </div>
            )}
            <Link to="/campaigns" data-testid="back-to-campaigns-link"
              className="inline-flex items-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-6 py-3 text-sm font-semibold transition-all duration-300">
              Back to Campaigns <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : status === 'expired' ? (
          <div className="glass p-12">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h2 className="font-display text-2xl font-bold mb-2">Session Expired</h2>
            <p className="text-[#9CA3AF] text-sm mb-6">The payment session has expired. Please try again.</p>
            <Link to="/campaigns" className="text-[#FF4343] text-sm hover:underline">Back to Campaigns</Link>
          </div>
        ) : (
          <div className="glass p-12">
            <XCircle className="w-16 h-16 text-[#FF4343] mx-auto mb-6" />
            <h2 className="font-display text-2xl font-bold mb-2">Payment Status Unknown</h2>
            <p className="text-[#9CA3AF] text-sm mb-6">We couldn't verify your payment. Please check your email for confirmation.</p>
            <Link to="/campaigns" className="text-[#FF4343] text-sm hover:underline">Back to Campaigns</Link>
          </div>
        )}
      </div>
    </div>
  );
}
